/**
 * Architecture Tab Content
 * Product browser for buildings and architecture items.
 * Uses click-to-place pattern via PlaceObjectTool.
 */

import './ArchitectureTabContent.css'
import type { TabContentRenderer } from './LeftPanel'
import { LAYER_CONFIG } from './LeftPanel'
import { ProductBrowser } from './ProductBrowser'
import { getViewer, getSelectedProduct, getPlacementMode } from './state'
import { startWalkthrough } from './Walkthrough'
import { t } from '@/i18n'

export class ArchitectureTabContent implements TabContentRenderer {
  private container: HTMLElement | null = null
  private productBrowser: ProductBrowser

  constructor() {
    this.productBrowser = new ProductBrowser({
      layer: 'architecture',
      accentColor: LAYER_CONFIG.architecture.color,
      showSearch: true,
      searchPlaceholder: t('architecture.searchPlaceholder', 'Search architecture...'),
      onProductSelected: (product) => {
        const viewer = getViewer()
        const toolManager = viewer?.getToolManager()
        if (product && toolManager) {
          // Preload GLB model so it's ready when user clicks to place
          viewer?.preloadProductModel(product.id)
          viewer?.ensureProductCached(product)
          toolManager.setActiveTool('place-object')
        } else if (!product && toolManager) {
          toolManager.setActiveTool('select')
        }
        this.updateHint()
      },
    })
  }

  render(container: HTMLElement): void {
    this.container = container
    this.renderContent()
  }

  destroy(): void {
    this.productBrowser.destroy()
    this.container = null
  }

  onActivate(): void {
    this.updateHint()
  }

  onDeactivate(): void {
    // nothing
  }

  private renderContent(): void {
    if (!this.container) return

    this.container.innerHTML = `
      <div class="architecture-tab-content">
        <!-- Product Browser -->
        <div class="architecture-section-title">
          <span>${t('architecture.catalog', 'Product Catalog')}</span>
          <button class="tab-help-btn" data-walkthrough="architecture" title="${t('tooltip.help', 'Help')}">
            <i class="fa-solid fa-circle-question"></i>
          </button>
        </div>
        <div class="architecture-product-browser"></div>

        <!-- Hint Bar -->
        <div class="architecture-hint-bar">
          <i class="fa-solid fa-circle-info"></i>
          <span class="architecture-hint-text">${this.getHintText()}</span>
        </div>
      </div>
    `

    // Render product browser
    const browserContainer = this.container.querySelector('.architecture-product-browser') as HTMLElement
    if (browserContainer) {
      this.productBrowser.render(browserContainer)
    }

    // Wire help button
    this.container.querySelector('.tab-help-btn')?.addEventListener('click', () => startWalkthrough('architecture'))
  }

  private updateHint(): void {
    if (!this.container) return
    const hintEl = this.container.querySelector('.architecture-hint-text')
    if (hintEl) {
      hintEl.textContent = this.getHintText()
    }
  }

  private getHintText(): string {
    const selectedProduct = getSelectedProduct()
    const mode = getPlacementMode()
    if (selectedProduct) {
      if (mode === 'continuous') {
        return t('architecture.hintContinuous', `Click to place ${selectedProduct.name} (Esc to stop)`)
      }
      return t('architecture.hintPlace', `Click on canvas to place ${selectedProduct.name}`)
    }
    return t('architecture.hintDefault', 'Select a product, then click on canvas to place')
  }
}
